import React, { useEffect, useMemo } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useNavigate, useParams } from "react-router-dom";
import { useShopifySaveBar } from "../../../hooks/useShopifySaveBar";
import { NavigationBlocker } from "../../../components/common/NavigationBlocker";
import { PageBreadcrumbs } from "../../../components/common/PageBreadcrumbs";
import {
  useOneClickEditorStore,
  selectOneClickIsDirty,
} from "../../../store/useOneClickEditorStore";
import {
  useOffersListStore,
  selectIsLimitReached,
} from "../../../store/useOffersListStore";
import { buildPreviewProduct } from "../../../store/one-click-editor.mapping";
import { SalesBoosterTabBar } from "../SalesBoosterTabBar";
import {
  NameSection,
  TriggerSection,
  ProductDiscountSection,
} from "./OneClickEditorConfig";
import { PopupTextsSection, ColorsSection } from "./OneClickEditorDesign";
import { BehaviorSection, CountdownSection } from "./OneClickEditorBehavior";
import {
  AcceptButtonSection,
  DeclineButtonSection,
} from "./OneClickEditorButtons";
import { OneClickEditorPreview } from "./OneClickEditorPreview";

// ─── Page ─────────────────────────────────────────────────────────────────────

const OneClickEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();

  // Editor state from the store
  const internalName = useOneClickEditorStore((s) => s.config.internalName);
  const isActive = useOneClickEditorStore((s) => s.config.isActive);
  const placement = useOneClickEditorStore((s) => s.config.placement);
  const triggerType = useOneClickEditorStore((s) => s.config.triggerType);
  const triggerProducts = useOneClickEditorStore(
    (s) => s.config.triggerProducts
  );
  const discountType = useOneClickEditorStore((s) => s.config.discountType);
  const discountValue = useOneClickEditorStore((s) => s.config.discountValue);
  const selectedProduct = useOneClickEditorStore(
    (s) => s.config.selectedProduct
  );
  const titleTemplate = useOneClickEditorStore((s) => s.config.titleTemplate);
  const subtitleTemplate = useOneClickEditorStore(
    (s) => s.config.subtitleTemplate
  );
  const productTitleOverride = useOneClickEditorStore(
    (s) => s.config.productTitleOverride
  );
  const description = useOneClickEditorStore((s) => s.config.description);
  const titleColor = useOneClickEditorStore((s) => s.config.titleColor);
  const priceColor = useOneClickEditorStore((s) => s.config.priceColor);
  const enableQuantitySelector = useOneClickEditorStore(
    (s) => s.config.enableQuantitySelector
  );
  const hideCloseIcon = useOneClickEditorStore((s) => s.config.hideCloseIcon);
  const hideVariantSelector = useOneClickEditorStore(
    (s) => s.config.hideVariantSelector
  );
  const countdownLabel = useOneClickEditorStore((s) => s.config.countdownLabel);
  const countdownHours = useOneClickEditorStore((s) => s.config.countdownHours);
  const countdownMinutes = useOneClickEditorStore(
    (s) => s.config.countdownMinutes
  );
  const countdownSeconds = useOneClickEditorStore(
    (s) => s.config.countdownSeconds
  );
  const acceptStyle = useOneClickEditorStore((s) => s.config.acceptStyle);
  const declineStyle = useOneClickEditorStore((s) => s.config.declineStyle);

  // Status
  const offerId = useOneClickEditorStore((s) => s.offerId);
  const initialIsActive = useOneClickEditorStore((s) => s.initialIsActive);
  const isSaving = useOneClickEditorStore((s) => s.isSaving);
  const isLoading = useOneClickEditorStore((s) => s.isLoading);
  const isDirty = useOneClickEditorStore(selectOneClickIsDirty);

  // Actions
  const updateField = useOneClickEditorStore((s) => s.updateField);
  const initNew = useOneClickEditorStore((s) => s.initNew);
  const loadFromBackend = useOneClickEditorStore((s) => s.loadFromBackend);
  const save = useOneClickEditorStore((s) => s.save);
  const discardChanges = useOneClickEditorStore((s) => s.discardChanges);
  const reset = useOneClickEditorStore((s) => s.reset);

  // Modal active-limit context (drives the activation block)
  const loadOffers = useOffersListStore((s) => s.loadOffers);
  const refetchOffers = useOffersListStore((s) => s.refetch);
  const isLimitReached = useOffersListStore(selectIsLimitReached);

  // Memoize the preview product so the modal preview only re-renders when
  // the inputs that affect it change. The mapping helper handles the
  // discount-application logic (kept out of the component for testability).
  const previewProduct = useMemo(
    () => buildPreviewProduct(selectedProduct, discountType, discountValue),
    [selectedProduct, discountType, discountValue]
  );

  // The merchant can toggle activation ON only if either (a) we're under the
  // backend active limit, or (b) this offer was already active when loaded
  // (saving it still active doesn't increase the count).
  const canActivateDraft = !isLimitReached || initialIsActive;
  // Disable the switch only when it's currently OFF and flipping it ON would
  // violate the limit. If the switch is ON, merchants can always turn it off.
  const isActiveSwitchDisabled = !canActivateDraft && !isActive;

  // Load or init when the route id changes
  useEffect(() => {
    if (routeId) {
      if (offerId !== routeId) {
        loadFromBackend(routeId).catch(() => {
          // Error is surfaced via store.error and toast (on save) — on load we
          // keep the user on the page with the default state so they can retry.
        });
      }
      return;
    }

    // /new route. Only start a fresh draft when the store is also fresh —
    // otherwise we'd wipe the state that `save()` just persisted while
    // `navigate()` is mid-flight to the new :id URL.
    if (offerId === null) {
      initNew();
    }
  }, [routeId, offerId, loadFromBackend, initNew]);

  // Keep the offers-list store warm so the active-limit check is accurate even
  // when the editor is deep-linked.
  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  // Reset on unmount so state doesn't leak across navigations
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  useShopifySaveBar({
    isDirty,
    isSaving,
    onSave: async () => {
      const wasNew = offerId === null;
      const savedId = await save();
      // Refresh the list store so the active counter reflects this mutation
      // when the merchant navigates back. Fire-and-forget — a failed refetch
      // shouldn't block the save confirmation.
      refetchOffers().catch(() => {});
      if (wasNew) {
        navigate(`/sales-booster/upsells-1-click/${savedId}`, {
          replace: true,
        });
      }
    },
    onDiscard: discardChanges,
  });

  return (
    <div>
      <NavigationBlocker isDirty={isDirty} />

      <s-page heading={internalName || "Nuevo upsell"}>
        <PageBreadcrumbs
          items={[
            { label: "Impulsor de ventas", to: "/sales-booster" },
            { label: "Upsells 1-Click", to: "/sales-booster/upsells-1-click" },
          ]}
        />
        <s-stack direction="block" gap="large-200">
          {/* ── Tab bar ── */}
          <SalesBoosterTabBar />

          {/* ── Header (active toggle + status) ── */}
          <div className="flex items-center justify-end py-1">
            <div className="flex items-center gap-3">
              <s-switch
                checked={isActive}
                disabled={isActiveSwitchDisabled}
                labelAccessibilityVisibility="exclusive"
                onChange={(e: CallbackEvent<"s-switch">) =>
                  updateField("isActive", e.currentTarget.checked)
                }
              />
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide border ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-gray-100 text-gray-500 border-gray-200"
                }`}
              >
                {isActive ? "Activo" : "Inactivo"}
              </span>
            </div>
          </div>

          {isActiveSwitchDisabled && (
            <s-banner tone="warning">
              <s-text>
                Has alcanzado el máximo de upsells activos. Desactiva uno
                existente para poder activar este.
              </s-text>
            </s-banner>
          )}

          {isLoading && (
            <s-banner tone="info">Cargando configuración del upsell…</s-banner>
          )}

          {/* ── Two-column layout ── */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            {/* Left: settings */}
            <s-stack direction="block" gap="base">
              <NameSection
                value={internalName}
                onChange={(v) => updateField("internalName", v)}
              />
              <TriggerSection
                value={triggerType}
                onChange={(v) => updateField("triggerType", v)}
                products={triggerProducts}
                onProductsChange={(v) => updateField("triggerProducts", v)}
                placement={placement}
                onPlacementChange={(v) => updateField("placement", v)}
              />
              <ProductDiscountSection
                selectedProduct={selectedProduct}
                onProductSelect={(v) => updateField("selectedProduct", v)}
                discountType={discountType}
                onDiscountTypeChange={(v) => updateField("discountType", v)}
                discountValue={discountValue}
                onDiscountValueChange={(v) => updateField("discountValue", v)}
              />
              <PopupTextsSection
                titleTemplate={titleTemplate}
                onTitleChange={(v) => updateField("titleTemplate", v)}
                subtitleTemplate={subtitleTemplate}
                onSubtitleChange={(v) => updateField("subtitleTemplate", v)}
                productTitleOverride={productTitleOverride}
                onProductTitleChange={(v) =>
                  updateField("productTitleOverride", v)
                }
                description={description}
                onDescriptionChange={(v) => updateField("description", v)}
              />
              <ColorsSection
                titleColor={titleColor}
                onTitleColorChange={(v) => updateField("titleColor", v)}
                priceColor={priceColor}
                onPriceColorChange={(v) => updateField("priceColor", v)}
              />
              <BehaviorSection
                enableQuantitySelector={enableQuantitySelector}
                onEnableQuantitySelectorChange={(v) =>
                  updateField("enableQuantitySelector", v)
                }
                hideCloseIcon={hideCloseIcon}
                onHideCloseIconChange={(v) => updateField("hideCloseIcon", v)}
                hideVariantSelector={hideVariantSelector}
                onHideVariantSelectorChange={(v) =>
                  updateField("hideVariantSelector", v)
                }
              />
              <CountdownSection
                label={countdownLabel}
                onLabelChange={(v) => updateField("countdownLabel", v)}
                hours={countdownHours}
                onHoursChange={(v) => updateField("countdownHours", v)}
                minutes={countdownMinutes}
                onMinutesChange={(v) => updateField("countdownMinutes", v)}
                seconds={countdownSeconds}
                onSecondsChange={(v) => updateField("countdownSeconds", v)}
              />
              <AcceptButtonSection
                style={acceptStyle}
                onChange={(updated) =>
                  updateField("acceptStyle", { ...acceptStyle, ...updated })
                }
              />
              <DeclineButtonSection
                style={declineStyle}
                onChange={(updated) =>
                  updateField("declineStyle", { ...declineStyle, ...updated })
                }
              />
            </s-stack>

            {/* Right: preview */}
            <OneClickEditorPreview
              product={previewProduct}
              titleTemplate={titleTemplate}
              subtitleTemplate={subtitleTemplate}
              productTitleOverride={productTitleOverride}
              description={description}
              titleColor={titleColor}
              priceColor={priceColor}
              acceptStyle={acceptStyle}
              declineStyle={declineStyle}
              hideCloseIcon={hideCloseIcon}
              enableQuantitySelector={enableQuantitySelector}
              hideVariantSelector={hideVariantSelector}
              countdownLabel={countdownLabel}
              countdownHours={countdownHours}
              countdownMinutes={countdownMinutes}
              countdownSeconds={countdownSeconds}
            />
          </div>
        </s-stack>
      </s-page>
    </div>
  );
};

export default OneClickEditor;
